import { AsyncPipe, isPlatformBrowser, NgClass, SlicePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  input,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { I18NEXT_SERVICE, I18NextModule, ITranslationService } from 'angular-i18next';
import { BehaviorSubject, debounceTime, interval, Observable, Subscription, take, tap, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserService } from '../../../common/user/user.service';
import { ViewportDirective } from '../../../common/viewport/viewport.directive';

@Component({
  selector: 'sharry-hero',
  standalone: true,
  imports: [NgClass, I18NextModule, ViewportDirective, AsyncPipe, RouterLink, MatButtonModule, SlicePipe],
  templateUrl: './hero.component.html',
  styleUrl: './hero.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroComponent implements OnDestroy {
  translateService = inject<ITranslationService>(I18NEXT_SERVICE);
  userService = inject(UserService);
  resolutions = input<Record<string, any>>({
    ['480']: '',
    ['768']: '',
    ['1024']: '',
    ['1440']: '',
    ['1920']: '',
  });
  resolutionsKeys = computed(() => Object.keys(this.resolutions()).map(r => Number(r)));
  @Input() backGroundImages: Record<string, any> &
    {
      title?: string;
      title$?: Observable<string>;
      subtitle: string;
      textColor: string;
    }[];
  @Input() thumb: string;
  @Input() shortHero = false;
  @Input() fullHero = false;
  @Input() component = '';
  @Input() showSignUp = false;
  @Input() h1Black = false;
  @Input() interval = 3000;
  @Input() animate = false;
  changeImageSubscription: Subscription = new Subscription();
  public env = environment.env;
  thumbLoaded = false;
  private platformId = inject(PLATFORM_ID);
  private renderer = inject(Renderer2);
  private destroyRef = inject(DestroyRef);
  private indexHero = new BehaviorSubject(-1);
  indexHero$ = this.indexHero.asObservable();

  constructor(private cd: ChangeDetectorRef) {}

  onImageLoad() {
    if (isPlatformBrowser(this.platformId)) {
      timer(1)
        .pipe(take(1))
        .subscribe(() => {
          this.indexHero.next(0);
        });
      timer(200)
        .pipe(take(1))
        .subscribe(() => {
          if (this.animate) {
            const picture: HTMLPictureElement = Array.from(document.querySelectorAll('.hero__picture'))[
              this.indexHero.value
            ] as HTMLPictureElement;
            if (picture) {
              if (this.indexHero.value % 2 === 0) {
                this.renderer.removeClass(picture, 'hero__picture--zoom-out');
                this.renderer.addClass(picture, 'hero__picture--zoom-out');
              } else {
                this.renderer.removeClass(picture, 'hero__picture--zoom-in');
                this.renderer.addClass(picture, 'hero__picture--zoom-in');
              }
            }
          }
        });
      this.changeImageSubscription = interval(this.interval)
        .pipe(
          tap(() => {
            if (this.indexHero.value < this.backGroundImages.length - 1) {
              this.indexHero.next(this.indexHero.value + 1);
            } else {
              this.indexHero.next(0);
            }
            this.cd.detectChanges();
          }),
          debounceTime(200),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({
          next: () => {
            if (this.animate) {
              const picture: HTMLPictureElement = Array.from(document.querySelectorAll('.hero__picture'))[
                this.indexHero.value
              ] as HTMLPictureElement;
              if (!this.indexHero.value || this.indexHero.value % 2 === 0) {
                this.renderer.removeClass(picture, 'hero__picture--zoom-out');
                this.renderer.addClass(picture, 'hero__picture--zoom-out');
              } else {
                this.renderer.removeClass(picture, 'hero__picture--zoom-in');
                this.renderer.addClass(picture, 'hero__picture--zoom-in');
              }
            }
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.changeImageSubscription.unsubscribe();
  }
}
